import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { TextZoom } from '@capacitor/text-zoom';
import { AppLauncher } from '@capacitor/app-launcher';
import * as Badge from './badge';
import * as Biometric from './biometric';
import * as CodePush from './code-push';
import * as Copy from './copy';
import * as Download from './download';
import * as Geolocation from './geolocation';
import * as Http from './http';
import * as Icon from './icon';
import * as Network from './network';
import * as Notification from './notification';
import * as PushNotification from './push-notification';
import * as Share from './share';
import * as Preferences from './preferences';
import * as VoiceRecorder from './voice-recorder';
import * as WalletPass from './wallet-pass';
import { processInternalRoute } from './utils';
import { shouldRouteDeepLink } from './incoming-share';
import { formatEmail } from '../email';
import { Device } from '@capacitor/device';
// NFC Launch, Android only
import { NFCLaunch } from 'capacitor-nfc-launch';
import { SafeArea } from 'capacitor-plugin-safe-area';

const USER_EMAIL_STORAGE_KEY = 'userEmail';
const USER_NAME_STORAGE_KEY = 'userName';

if (Capacitor.isNativePlatform()) {
  // Do not respect to accessibility's font size
  TextZoom.set({ value: 1 })
    .then()
    .catch();
}

const isNativePlatform = () => Capacitor.isNativePlatform();
const getPlatform = () => Capacitor.getPlatform();
const getAppInfo = async () => await App.getInfo();
const isIOSPlatform = () => Capacitor.getPlatform() === 'ios';
const shouldShowEnterpriseDialog = userDetails => {
  if (isIOSPlatform()) {
    if (userDetails && userDetails.email) {
      const formattedEmail = formatEmail(userDetails.email);
      if (formattedEmail === 'pfp.kyc@googlemail.com' || formattedEmail === 'pfpky.c@googlemail.com') {
        return true;
      }
    }
  }

  return false;
};
const shouldEnterpriseDialogBlock = userDetails => {
  if (isIOSPlatform()) {
    if (userDetails && userDetails.email) {
      const formattedEmail = formatEmail(userDetails.email);
      if (formattedEmail === 'pfpky.c@googlemail.com') {
        return true;
      }
    }
  }

  return false;
};

const addDeepLinkListener = history => {
  App.addListener('appUrlOpen', event => {
    if (!shouldRouteDeepLink(event.url)) {
      return;
    }
    // WeChat SDK callback URLs contain /wx<appId>/ in the path or _wechat_sdk_biz_data
    // in the query. Ignore them — they are handled natively by CapgoCapacitorWechat.
    if (/\/wx[0-9a-f]+\//i.test(event.url) || event.url.includes('_wechat_sdk_biz_data')) {
      return;
    }
    processInternalRoute(event.url);
  });
};

const addNFCListener = histroy => {
  // Due to appUrlOpen is for link click and not for NFC
  // We need to add NFC listener here

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    NFCLaunch.addListener('message', data => {
      const eotRegExp = new RegExp('\u0004', 'g');
      const cleanedData = data.message.replace(eotRegExp, '');
      try {
        if (
          (cleanedData && cleanedData.startsWith('app.portfoplus.com')) ||
          cleanedData.startsWith('beta-app.portfoplus.com') ||
          cleanedData.startsWith('lk.portfoplus.com') ||
          cleanedData.startsWith('beta-lk.portfoplus.com')
        ) {
          processInternalRoute(cleanedData);
        }
      } catch (error) {
        console.error('Error processing NFC message: ', error);
      }
    });
  }
};

const addBackListener = history => {
  App.addListener('backButton', data => {
    history.goBack();
  });
};

const saveUserEmail = async email => {
  if (email) {
    if (isNativePlatform()) {
      return Preferences.saveStorage(USER_EMAIL_STORAGE_KEY, email);
    } else {
      return undefined;
    }
  }
};

const getUserEmail = async () => {
  if (isNativePlatform()) {
    return Preferences.getStorage(USER_EMAIL_STORAGE_KEY);
  } else {
    return undefined;
  }
};

const removeUserEmail = async () => {
  if (isNativePlatform()) {
    return Preferences.removeStorage(USER_EMAIL_STORAGE_KEY);
  } else {
    return undefined;
  }
};

const saveUserName = async name => {
  if (name) {
    if (isNativePlatform()) {
      return Preferences.saveStorage(USER_NAME_STORAGE_KEY, name);
    } else {
      return undefined;
    }
  }
};

const getUserName = async () => {
  if (isNativePlatform()) {
    return Preferences.getStorage(USER_NAME_STORAGE_KEY);
  } else {
    return undefined;
  }
};

const removeUserName = async () => {
  if (isNativePlatform()) {
    return Preferences.removeStorage(USER_NAME_STORAGE_KEY);
  } else {
    return undefined;
  }
};

const getOSVersion = async () => {
  const info = await Device.getInfo();
  if (info && info.osVersion) {
    console.log('getOSVersion: ', info.osVersion);
    return info.osVersion;
  } else if (info && info.operatingSystem) {
    return info.operatingSystem;
  }
  return undefined;
};

const isAndroid15orAbove = async () => {
  if (Capacitor.getPlatform() === 'android') {
    const osVersion = await getOSVersion();
    if (osVersion) {
      const version = parseInt(osVersion.split('.')[0], 10);
      console.log('isAndroid15orAbove: ', version);
      return version >= 15;
    }
  }
  return false;
};

const setSafeAreaInsets = async () => {
  const shouldSetSafeAreaInsets = await isAndroid15orAbove();

  if (shouldSetSafeAreaInsets) {
    const safeAreaTop = await SafeArea.getStatusBarHeight();
    const safeAreaBottom = await SafeArea.getSafeAreaInsets();

    if (safeAreaTop) {
      document.documentElement.style.setProperty('--top-padding', `${safeAreaTop.statusBarHeight}px`);
    }
    if (safeAreaBottom) {
      document.documentElement.style.setProperty('--ion-safe-area-bottom', `${safeAreaBottom.insets.bottom}px`);
      document.documentElement.style.setProperty('--bottom-padding', `${safeAreaBottom.insets.bottom}px`);
    }
  }
};

const NativeOrWeb = {
  setSafeAreaInsets,
  getOSVersion,
  addNFCListener,
  addDeepLinkListener,
  addBackListener,
  isNativePlatform,
  getPlatform,
  getAppInfo,
  isIOSPlatform,
  shouldShowEnterpriseDialog,
  shouldEnterpriseDialogBlock,
  saveUserName,
  getUserName,
  removeUserName,
  saveUserEmail,
  getUserEmail,
  removeUserEmail,
  canOpenUrl: AppLauncher.canOpenUrl,
  openUrl: AppLauncher.openUrl,
  ...Badge,
  ...Biometric,
  ...CodePush,
  ...Copy,
  ...Download,
  ...Geolocation,
  ...Http,
  ...Icon,
  ...Network,
  ...Notification,
  ...PushNotification,
  ...Share,
  ...Preferences,
  ...VoiceRecorder,
  ...WalletPass
};

export default NativeOrWeb;
